import React, { useEffect, useState } from 'react';
import { bool, arrayOf, number, shape } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER, propTypes } from '../../util/types';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { withViewport } from '../../util/uiHelpers';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Heading,
  H2,
  H4,
  Page,
  AvatarLarge,
  NamedLink,
  ListingCard,
  Reviews,
  ButtonTabNavHorizontal,
  LayoutSideNavigation,
  LayoutSingleColumn,
  CategoryListingCard,
  NamedRedirect,
  SearchListingCard,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import css from './ProfilePage.module.css';
import { hasQueryParam, redirectToStore } from '../../util/urlHelpers';
import { useHistory } from 'react-router-dom';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

const MAX_MOBILE_SCREEN_WIDTH = 768;
const CATEGORY_PARAM = 'pub_category';

export const AsideContent = props => {
  const { user, displayName, isCurrentUser } = props;
  return (
    <div className={css.asideContent}>
      <AvatarLarge className={css.avatar} user={user} disableProfileLink />
      <H2 as="h1" className={css.mobileHeading}>
        {displayName ? (
          <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
        ) : null}
      </H2>
      {isCurrentUser ? (
        <>
          <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
          </NamedLink>
          <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
          </NamedLink>
        </>
      ) : null}
    </div>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const MobileReviews = props => {
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  return (
    <div className={css.mobileReviews}>
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsFromMyCustomersTitle"
          values={{ count: reviewsOfProvider.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfProvider} />
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsAsACustomerTitle"
          values={{ count: reviewsOfCustomer.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfCustomer} />
    </div>
  );
};

export const DesktopReviews = props => {
  const [showReviewsType, setShowReviewsType] = useState(REVIEW_TYPE_OF_PROVIDER);
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  const isReviewTypeProviderSelected = showReviewsType === REVIEW_TYPE_OF_PROVIDER;
  const isReviewTypeCustomerSelected = showReviewsType === REVIEW_TYPE_OF_CUSTOMER;
  const desktopReviewTabs = [
    {
      text: (
        <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsFromMyCustomersTitle"
            values={{ count: reviewsOfProvider.length }}
          />
        </Heading>
      ),
      selected: isReviewTypeProviderSelected,
      onClick: () => setShowReviewsType(REVIEW_TYPE_OF_PROVIDER),
    },
    {
      text: (
        <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsAsACustomerTitle"
            values={{ count: reviewsOfCustomer.length }}
          />
        </Heading>
      ),
      selected: isReviewTypeCustomerSelected,
      onClick: () => setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER),
    },
  ];

  return (
    <div className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <ButtonTabNavHorizontal className={css.desktopReviewsTabNav} tabs={desktopReviewTabs} />

        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />

        {isReviewTypeProviderSelected ? (
          <Reviews reviews={reviewsOfProvider} />
        ) : (
          <Reviews reviews={reviewsOfCustomer} />
        )}
      </div>
    </div>
  );
};

export const MainContent = props => {
  const {
    userShowError,
    bio,
    displayName,
    listings,
    queryListingsError,
    reviews,
    queryReviewsError,
    viewport,
    isIframe,
    profileTitle,
    currentCategory,
    categories,
    isCurrentUser,
  } = props;

  const hasListings = listings.length > 0;
  const isMobileLayout = viewport.width < MAX_MOBILE_SCREEN_WIDTH;
  const hasBio = !!bio;

  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }

  const reviewsContainer = !isIframe ? (
    isMobileLayout ? (
      <MobileReviews reviews={reviews} queryReviewsError={queryReviewsError} />
    ) : (
      <DesktopReviews reviews={reviews} queryReviewsError={queryReviewsError} />
    )
  ) : null;

  const setCategory = selectedCategory => {
    if (props?.location?.search) {
      let searchString = props.location.search;
      if (searchString.length && searchString[0] == '?') {
        searchString = searchString.slice(1);
      }

      let redirect = false;

      if (searchString.length) {
        let searchParamsItems = searchString.split('&');
        for (let si = 0; si < searchParamsItems.length; si++) {
          let searchItem = searchParamsItems[si];
          searchItem = searchItem.split('=');
          if (searchItem.length && searchItem[0] !== undefined && searchItem[0] == CATEGORY_PARAM) {
            if (searchItem[1] !== undefined) {
              if (searchItem[1] !== selectedCategory) {
                searchItem[1] = selectedCategory;
                searchParamsItems[si] = searchItem.join('=');

                searchString = '?' + searchParamsItems.join('&');
                redirect = true;
              }
            }
          }
        }
      }

      if (!redirect) {
        const noCategory = !searchString.includes(CATEGORY_PARAM);
        if (noCategory) {
          searchString = `?${searchString}&${CATEGORY_PARAM}=${selectedCategory}`;
          searchString = searchString.replace('&&', '&');
          redirect = true;
        }
      }

      if (redirect) {
        const searchParams = Object.fromEntries(new URLSearchParams(searchString));
        history.push(
          createResourceLocatorString(
            'ProfilePage',
            routeConfiguration,
            { id: props.params.id },
            searchParams
          )
        );
      }
    }
  };

  return (
    <div>
      <H2 as="h1" className={css.desktopHeading}>
        {profileTitle}
      </H2>
      {hasBio ? <p className={css.bio}>{bio}</p> : null}
      {hasListings ? (
        <div className={listingsContainerClasses}>
          {!isIframe ? (
            <H4 as="h2" className={css.listingsTitle}>
              <FormattedMessage
                id="ProfilePage.listingsTitle"
                values={{ count: listings.length }}
              />
            </H4>
          ) : null}
          {isIframe && categories.length ? (
            <div className={css.categoriesSelector}>
              <div
                onClick={() => setCategory('all')}
                className={currentCategory == 'all' ? css.selectedCategory : null}
              >
                All
              </div>
              {categories.map(cat => (
                <div
                  key={cat.option}
                  onClick={() => setCategory(cat.option)}
                  className={currentCategory == cat.option ? css.selectedCategory : null}
                >
                  {cat.label}
                </div>
              ))}
            </div>
          ) : null}
          <ul className={css.listings}>
            {listings.map(l => (
              <li className={css.listing} key={l.id.uuid}>
                <SearchListingCard
                  listing={l}
                  isIframe={isIframe}
                  showAuthorInfo={false}
                  showActions={isCurrentUser}
                />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {reviewsContainer}
    </div>
  );
};

const ProfilePageComponent = props => {
  const config = useConfiguration();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const {
    scrollingDisabled,
    currentUser,
    userShowError,
    user,
    intl,
    currentCategory,
    categories,
    ...rest
  } = props;
  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const profileUser = ensureUser(user);
  const isCurrentUser =
    ensuredCurrentUser.id && profileUser.id && ensuredCurrentUser.id.uuid === profileUser.id.uuid;
  const { bio, displayName, publicData } = profileUser?.attributes?.profile || {};
  const { greetingHeading } = publicData || {};

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);
  let profileTitle = 'Loading store';
  if (displayName) {
    profileTitle = intl.formatMessage({ id: 'ProfilePage.desktopHeading' }, { name: displayName });

    if (greetingHeading) {
      profileTitle = greetingHeading.replace('{name}', displayName);
    }
  }

  if (userShowError && userShowError.status === 404) {
    return <NotFoundPage />;
  }

  useEffect(() => {
    if (profileUser && profileUser?.id?.uuid) {
      redirectToStore({ redirect: true, history, routeConfiguration, id: profileUser.id.uuid });
    }
  }, [history, profileUser, routeConfiguration]);

  const isIframe = hasQueryParam(props.location, 'iframe');
  const mainContent = (
    <MainContent
      bio={bio}
      displayName={displayName}
      profileTitle={profileTitle}
      userShowError={userShowError}
      currentCategory={currentCategory}
      categories={categories}
      isIframe={isIframe}
      isCurrentUser={isCurrentUser}
      {...rest}
    />
  );

  const singleColumn = (
    <LayoutSingleColumn className={css.pageRoot} topbar={<></>} footer={null}>
      {mainContent}
    </LayoutSingleColumn>
  );
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        name: schemaTitle,
      }}
    >
      {isIframe ? (
        singleColumn
      ) : (
        <LayoutSideNavigation
          sideNavClassName={css.aside}
          topbar={<TopbarContainer currentPage="ProfilePage" />}
          sideNav={
            <AsideContent user={user} isCurrentUser={isCurrentUser} displayName={displayName} />
          }
          footer={<FooterContainer />}
        >
          {mainContent}
        </LayoutSideNavigation>
      )}
    </Page>
  );
};

ProfilePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
};

ProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  user: propTypes.user,
  userShowError: propTypes.error,
  queryListingsError: propTypes.error,
  listings: arrayOf(propTypes.listing).isRequired,
  reviews: arrayOf(propTypes.review),
  queryReviewsError: propTypes.error,

  // form withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews,
    queryReviewsError,
    categories,
    currentCategory,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;
  const listings = getMarketplaceEntities(state, userListingRefs);

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    user,
    userShowError,
    queryListingsError,
    listings,
    reviews,
    queryReviewsError,
    categories,
    currentCategory,
  };
};

const ProfilePage = compose(
  connect(mapStateToProps),
  withViewport,
  injectIntl
)(ProfilePageComponent);

export default ProfilePage;
